/**
 * What a meeting may be scheduled for.
 *
 * The form has always refused a start time in the past — but only on submit,
 * after the user had picked one, filled in everything else and pressed the
 * button. The pickers themselves offered yesterday and this morning as though
 * they were ordinary choices. These are the rules the pickers ask, so an
 * impossible time cannot be chosen in the first place.
 */

/** Midnight at the start of `d`, which is the earliest date still schedulable. */
export function startOfDay(d: Date): Date {
  const out = new Date(d)
  out.setHours(0, 0, 0, 0)
  return out
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

const pad = (n: number) => String(n).padStart(2, '0')

/**
 * The earliest time that may be chosen on `date`, as "HH:mm", or null when the
 * whole day is still ahead.
 *
 * Rounded UP to the picker's granularity, so the answer is a slot the drum
 * actually has: with a 5-minute step, 10:31 gives 10:35 rather than a minute
 * the user cannot land on.
 *
 * Capped at the last slot of the day. Scheduling something in the final few
 * minutes before midnight is the one case this cannot express — the day has no
 * later slot — and the submit-time check still refuses it, which is the same
 * answer, just later.
 */
export function earliestTimeFor(
  date: Date | undefined,
  now: Date,
  step: number,
): string | null {
  if (!date || !isSameDay(date, now)) return null

  const minutesNow = now.getHours() * 60 + now.getMinutes()
  const lastSlot = Math.floor((24 * 60 - 1) / step) * step
  const snapped = Math.min(Math.ceil(minutesNow / step) * step, lastSlot)

  return `${pad(Math.floor(snapped / 60))}:${pad(snapped % 60)}`
}

/** Is "HH:mm" earlier than "HH:mm"? Absent values are never too early. */
export function isBefore(time: string | undefined, min: string | null): boolean {
  if (!time || !min) return false
  const [h, m] = time.split(':').map(Number)
  const [mh, mm] = min.split(':').map(Number)
  if (![h, m, mh, mm].every(Number.isFinite)) return false
  return h * 60 + m < mh * 60 + mm
}
